/*
  Collection page: filter drawer (slides in from the right), sort dropdown, active filter chips
  and the "load more" button.

  Filters and sort re-render the results with the Section Rendering API (?section_id=...), so the page
  never reloads and the address bar always holds a shareable link. Chips, "clear all" and "load more"
  are real links, so they still work if this script does not run.

  The drawer is a native <dialog>: showModal() gives the focus trap, Esc handling and an inert page behind it.
  Close with the X button, a tap outside the panel, Esc, or the show results button.
*/
if (!customElements.get('collection-filters')) {
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-filters-form]');
      this.opener = this.querySelector('[data-filter-open]');
      this.sortSelect = this.querySelector('[data-sort-select]');
      this.currentQuery = window.location.search.slice(1);

      this.addEventListener('click', (event) => this.onClick(event));

      if (this.sortSelect) this.sortSelect.addEventListener('change', () => this.apply());

      if (this.form) {
        // Checkboxes apply as soon as they change; price fields apply when the visitor leaves the field
        this.form.addEventListener('change', () => this.scheduleApply());
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.apply();
          this.closeDrawer();
        });
      }

      if (this.dialog && this.opener) {
        this.opener.addEventListener('click', () => this.openDrawer());

        // A tap on the dimmed area outside the panel lands on the dialog itself
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog || event.target.closest('[data-filter-close]')) this.closeDrawer();
        });

        // Esc: run the slide-out instead of closing instantly
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeDrawer();
        });

        this.dialog.addEventListener('close', () => this.onDrawerClosed());
      }

      this.onPopState = () => this.load(window.location.search.slice(1), { push: false });
      window.addEventListener('popstate', this.onPopState);

      // Back button restoring the page from the browser cache with the drawer still open
      this.onPageShow = (event) => {
        if (event.persisted && this.dialog && this.dialog.open) this.dialog.close();
      };
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('pageshow', this.onPageShow);
      clearTimeout(this.applyTimer);
      document.body.classList.remove('overflow-hidden');
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* ---------- Drawer ---------- */

    openDrawer() {
      if (this.dialog.open) return;
      document.body.classList.add('overflow-hidden');
      this.dialog.showModal();
      this.opener.setAttribute('aria-expanded', 'true');
      // Force layout so the slide-in transition starts from the closed position
      void this.dialog.offsetWidth;
      this.dialog.classList.add('is-open');
    }

    closeDrawer() {
      if (!this.dialog || !this.dialog.open || this.isClosing) return;
      this.isClosing = true;
      this.dialog.classList.remove('is-open');

      if (this.prefersReducedMotion) {
        this.finishClose();
        return;
      }

      const onEnd = (event) => {
        if (event.target === this.dialog && event.propertyName === 'transform') this.finishClose();
      };
      this.dialog.addEventListener('transitionend', onEnd);
      this.closeTimer = setTimeout(() => this.finishClose(), 450);
      this.removeEndListener = () => this.dialog.removeEventListener('transitionend', onEnd);
    }

    finishClose() {
      clearTimeout(this.closeTimer);
      if (this.removeEndListener) this.removeEndListener();
      this.removeEndListener = null;
      if (this.dialog.open) this.dialog.close();
    }

    onDrawerClosed() {
      this.isClosing = false;
      this.dialog.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      this.opener.setAttribute('aria-expanded', 'false');
      this.opener.focus();
    }

    /* ---------- Clicks on links inside the component ---------- */

    onClick(event) {
      const link = event.target.closest('[data-filter-link]');
      if (link) {
        // Let "open in new tab" clicks through
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.load(new URL(link.href, window.location.origin).search.slice(1), { trigger: link });
        return;
      }

      const more = event.target.closest('[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
      }
    }

    /* ---------- Filters and sort ---------- */

    scheduleApply() {
      clearTimeout(this.applyTimer);
      this.applyTimer = setTimeout(() => this.apply(), 300);
    }

    buildQuery() {
      const params = new URLSearchParams();

      if (this.form) {
        this.form.querySelectorAll('input[name]').forEach((input) => {
          if (input.disabled) return;
          if (input.type === 'checkbox' && !input.checked) return;
          let value = input.value.trim();
          if (input.hasAttribute('data-price-input')) value = this.normalizePrice(value);
          if (value === '') return;
          params.append(input.name, value);
        });
      }

      if (this.sortSelect && this.sortSelect.value !== this.dataset.defaultSort) {
        params.set('sort_by', this.sortSelect.value);
      }

      return params.toString();
    }

    // "10,50", "10.50" and "1.000,50" all become a plain decimal number, which is what the URL needs
    normalizePrice(text) {
      const value = text.replace(/\s/g, '');
      const lastComma = value.lastIndexOf(',');
      const lastDot = value.lastIndexOf('.');
      if (lastComma > lastDot) return value.replace(/\./g, '').replace(',', '.');
      return value.replace(/,/g, '');
    }

    apply() {
      clearTimeout(this.applyTimer);
      return this.load(this.buildQuery());
    }

    async load(query, { push = true, trigger = null } = {}) {
      if (query === this.currentQuery) return;

      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setLoading(true);

      const updateEvent = this.startUpdateEvent(query);
      const url = `${window.location.pathname}?section_id=${this.sectionId}${query ? `&${query}` : ''}`;

      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        this.update(doc, trigger);
        this.currentQuery = query;
        if (push) history.pushState({ query }, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

        const results = this.querySelector('[data-collection-results]');
        if (updateEvent) updateEvent.resolve(Number(results && results.dataset.productsCount) || 0);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        if (updateEvent) updateEvent.reject(error);
        // Fall back to a normal page load so the visitor still gets the filtered page
        window.location.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
      } finally {
        if (this.controller === controller) this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      const results = this.querySelector('[data-collection-results]');
      if (results) results.setAttribute('aria-busy', String(isLoading));
    }

    swap(selector, doc) {
      const current = this.querySelector(selector);
      const next = doc.querySelector(selector);
      if (current && next) current.replaceWith(next);
    }

    update(doc, trigger) {
      const triggerWasInDrawer = Boolean(trigger && this.dialog && this.dialog.contains(trigger));

      this.swap('[data-collection-results]', doc);
      this.swap('[data-collection-chips]', doc);
      this.swap('[data-filter-count]', doc);
      this.syncDrawer(doc);

      // Back and forward buttons: put the sort dropdown on the sort order of that page
      const nextSort = doc.querySelector('[data-sort-select]');
      if (this.sortSelect && nextSort) this.sortSelect.value = nextSort.value;

      // The link that was clicked is gone (chip) or hidden (clear all): move focus somewhere sensible
      if (trigger && (!trigger.isConnected || trigger.closest('[hidden]'))) {
        const target = triggerWasInDrawer
          ? this.querySelector('[data-show-results]')
          : this.querySelector('[data-chip]') || this.opener || this.sortSelect;
        if (target) target.focus();
      }

      this.scrollToTop();
    }

    syncDrawer(doc) {
      if (!this.form) return;

      const container = this.form.querySelector('[data-filter-groups]');
      const nextGroups = Array.from(doc.querySelectorAll('[data-filter-group]'));
      const keep = new Set(nextGroups.map((group) => group.dataset.filterGroup));
      const focusedId = this.form.contains(document.activeElement) ? document.activeElement.id : '';

      container.querySelectorAll('[data-filter-group]').forEach((group) => {
        if (!keep.has(group.dataset.filterGroup)) group.remove();
      });

      let previous = null;
      nextGroups.forEach((next) => {
        const current = container.querySelector(`[data-filter-group="${next.dataset.filterGroup}"]`);

        if (!current) {
          if (previous) previous.after(next);
          else container.prepend(next);
          previous = next;
          return;
        }

        // Keep the open/closed state of the group; refresh counts, disabled values and checked boxes
        const list = current.querySelector('[data-filter-list]');
        const nextList = next.querySelector('[data-filter-list]');
        if (list && nextList) list.innerHTML = nextList.innerHTML;

        current.querySelectorAll('[data-price-input]').forEach((input) => {
          const nextInput = next.querySelector(`[name="${input.name}"]`);
          if (!nextInput) return;
          if (input !== document.activeElement) input.value = nextInput.value;
          input.placeholder = nextInput.placeholder;
        });

        const count = current.querySelector('[data-group-count]');
        const nextCount = next.querySelector('[data-group-count]');
        if (count && nextCount) {
          count.textContent = nextCount.textContent;
          count.hidden = nextCount.hidden;
        }

        previous = current;
      });

      const results = this.form.querySelector('[data-results-count]');
      const nextResults = doc.querySelector('[data-results-count]');
      if (results && nextResults) results.textContent = nextResults.textContent;

      const status = this.form.querySelector('[data-status]');
      const nextStatus = doc.querySelector('[data-status]');
      if (status && nextStatus) status.textContent = nextStatus.textContent;

      const clear = this.form.querySelector('[data-clear-all]');
      const nextClear = doc.querySelector('[data-clear-all]');
      if (clear && nextClear) {
        clear.hidden = nextClear.hidden;
        clear.href = nextClear.href;
      }

      // The list was rebuilt, so put focus back on the same checkbox
      if (focusedId) {
        const again = document.getElementById(focusedId);
        if (again && again !== document.activeElement) again.focus();
      }
    }

    // After a filter or sort change, bring the top of the results back into view if the visitor has scrolled past it
    scrollToTop() {
      const header = document.querySelector('.section-header');
      const headerHeight = header ? header.offsetHeight : 0;
      const top = this.getBoundingClientRect().top + window.scrollY - headerHeight;
      if (window.scrollY > top) {
        window.scrollTo({ top, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
      }
    }

    /* ---------- Load more ---------- */

    async loadMore(link) {
      if (this.isLoadingMore) return;
      this.isLoadingMore = true;
      link.classList.add('is-busy');
      link.setAttribute('aria-busy', 'true');

      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const grid = this.querySelector('[data-product-grid]');
        const items = Array.from(doc.querySelectorAll('[data-collection-results] [data-product-item]'));
        if (!grid || items.length === 0) throw new Error('No products in the next page');
        items.forEach((item) => grid.appendChild(item));

        const wrap = this.querySelector('[data-load-more-wrap]');
        const nextWrap = doc.querySelector('[data-load-more-wrap]');
        if (wrap && nextWrap) wrap.replaceWith(nextWrap);
        else if (wrap) wrap.remove();

        const firstLink = items[0].querySelector('a');
        if (firstLink) firstLink.focus({ preventScroll: true });
      } catch (error) {
        console.error(error);
        // Fall back to the plain link, which opens the next page
        window.location.href = link.href;
      } finally {
        this.isLoadingMore = false;
        link.classList.remove('is-busy');
        link.removeAttribute('aria-busy');
      }
    }

    /* ---------- Shopify storefront analytics event (same as Dawn's facets.js) ---------- */

    startUpdateEvent(query) {
      const { CollectionUpdateEvent } = window.StandardEvents || {};
      if (!CollectionUpdateEvent) return null;

      const results = this.querySelector('[data-collection-results]');
      const searchParams = new URLSearchParams(query);
      const deferred = CollectionUpdateEvent.createPromise();

      // The id is null for automatic collections such as /collections/all
      this.dispatchEvent(
        new CollectionUpdateEvent({
          collection: {
            id: this.dataset.collectionId || null,
            handle: this.dataset.collectionHandle || '',
            productsCount: Number(results && results.dataset.productsCount) || 0,
          },
          productFilters: CollectionUpdateEvent.parseProductFilters(searchParams),
          sortKey: CollectionUpdateEvent.getSortKey(searchParams),
          promise: deferred.promise,
        }),
      );

      return {
        resolve: (productsCount) => deferred.resolve({ productsCount }),
        reject: deferred.reject,
      };
    }
  }

  customElements.define('collection-filters', CollectionFilters);
}
